import classNames from 'classnames'
import Scrim from './Scrim'
import FocusTrap from 'focus-trap-react'
import closeIcon from '@/icons/close-icon.svg'
import useScrollCapture from '@/hooks/use-scroll-capture'
import { ComponentChildren } from 'preact'
import { createPortal } from 'preact/compat'

/**
 * Preact modal component.
 * Modal Controller is required to open and close the modal.
 * Children are rendered inside the modal.
 * @param children - the content to display inside the modal
 * @param controller - the modal controller to manage the modal state
 * @param padding - the padding around the modal content, can be 'small', 'medium', or 'large'
 */
export default function Modal({ children, controller, padding = ModalPadding.LARGE }: ModalProps) {
  const isOpen = controller.isOpen
  useScrollCapture(isOpen)

  const paddingClass = classNames({
    'size-md:p-6': padding === ModalPadding.SMALL,
    'size-md:p-12': padding === ModalPadding.MEDIUM,
    'size-md:p-16': padding === ModalPadding.LARGE
  })

  return createPortal(
    <>
      <Scrim showScrim={isOpen} onClick={() => controller.close()} />
      <FocusTrap
        active={isOpen}
        focusTrapOptions={{
          clickOutsideDeactivates: true,
          escapeDeactivates: true,
          onPostDeactivate: () => controller.close()
        }}>
        <div
          className={classNames(
            'group/modal fixed left-1/2 top-1/2 z-50 flex h-full w-full -translate-x-1/2 -translate-y-1/2 justify-center bg-crisp-white pb-6 pt-12 shadow-2xl transition-all duration-300 ease-in-out size-md:h-[unset] size-md:max-h-[90vh] size-md:w-[unset] size-md:max-w-[90vw] size-md:rounded-2xl',
            paddingClass,
            {
              'pointer-events-none scale-75 opacity-0': !isOpen,
              'opacity-1  scale-100': isOpen
            }
          )}>
          <button
            onClick={e => {
              e.preventDefault()
              controller.close()
            }}
            type='button'
            className='absolute right-0 top-0'>
            <img src={closeIcon} alt='close drawer icon' />
          </button>
          <div className='max-h-full overflow-auto px-6'>{children}</div>
        </div>
      </FocusTrap>
    </>,
    document.body
  )
}

export enum ModalPadding {
  SMALL,
  MEDIUM,
  LARGE
}

export type ModalProps = {
  children: ComponentChildren
  controller: ModalController
  padding?: ModalPadding
}

/**
 * The modal controller interface
 * @param isOpen - if the modal is open
 * @param close - method to close the modal
 * @see Modal
 */
export type ModalController = {
  isOpen: boolean
  close: () => void
}
